import React, { useEffect } from 'react';
import { get, throttle } from 'lodash';
import memoizeOne from 'memoize-one';

// Types
import { MilanoteApplicationMode, PlatformDetails } from '../../../../common/platform/platformTypes';

// Constants
import { ELEMENT_DEFAULT_WIDTH } from '../../../../common/elements/elementConstants';

const LIST_PADDING = 40;
const MAX_MOBILE_WORKSPACE_WIDTH = 700;
const MAX_MOBILE_IN_LIST_ZOOM_SCALE = 1.2;

/**
 * Get memoized list container width for mobile.
 *
 * This value is memoized since it accesses window.innerWidth, which might cause a forced layout/reflow.
 * To invalidate the memoized value, pass `true` as the first argument.
 */
const getMemoizedMobileListContainerWidth = memoizeOne(
    (invalidate?: boolean) => {
        const listContainerWidth = Math.min(MAX_MOBILE_WORKSPACE_WIDTH, window.innerWidth);
        return listContainerWidth - LIST_PADDING;
    },
    (newInputs) => {
        const invalidate = get(newInputs, [0]) || false;
        return !invalidate;
    },
);

export const getZoomScaleInList = (
    platformDetails: PlatformDetails,
    gridSize: number,
    listContainerWidth: number = getMemoizedMobileListContainerWidth(),
) => {
    if (platformDetails.appMode !== MilanoteApplicationMode.mobile) return 1;

    const zoomScale = listContainerWidth / (ELEMENT_DEFAULT_WIDTH * gridSize);

    return Math.min(MAX_MOBILE_IN_LIST_ZOOM_SCALE, zoomScale);
};

/**
 * Scale list based on the zoom scale. This is necessary to ensure that the elements appear
 * in the correct dimensions on mobile, where width of the screen varies depending on device.
 */
const useScaledMobileScrollableList = (
    platformDetails: PlatformDetails,
    gridSize: number,
): {
    listZoomScale: number;
} => {
    const [listZoomScale, setListZoomScale] = React.useState<number>(() => {
        const listContainerWidth = getMemoizedMobileListContainerWidth(true);
        return getZoomScaleInList(platformDetails, gridSize, listContainerWidth);
    });

    useEffect(() => {
        const onResize = throttle(() => {
            const listContainerWidth = getMemoizedMobileListContainerWidth(true);
            // NOTE: This will use platformDetails and gridSize from the first render, but it's fine since these
            //   values doesn't change in mobile.
            const zoomScale = getZoomScaleInList(platformDetails, gridSize, listContainerWidth);

            setListZoomScale(zoomScale);
        }, 100);

        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, []);

    return {
        listZoomScale,
    };
};

export default useScaledMobileScrollableList;
